/**
 * 連続値の指標（客単価・平均滞在時間・1 人あたり PV など）の比較。
 *
 * 方針:
 *  - 等分散を仮定しない **Welch の t 検定**を使う。A/B テストでは施策側の
 *    ばらつきが変わることが普通で、等分散を仮定した Student の t 検定は
 *    人数が偏っているときに第一種の過誤が名目からずれる。
 *  - 自由度は Welch-Satterthwaite の近似で求める。整数にはならない。
 *  - 比率の検定と違い、検定にも区間推定にも同じ標準誤差を使う。
 *    平均の帰無仮説 H0: μ1 = μ2 は分散に制約を与えないため、
 *    プールする理由がない（比率では p1 = p2 が分散まで決めるので事情が違う）。
 */

import { studentTCdf, studentTQuantile, studentTTwoSided } from './student'
import { normalQuantile } from './normal'
import type { Interval, Tail } from './types'

/** 1 群の要約統計量。生データではなくこの 3 つだけを入力にする。 */
export interface SampleSummary {
  /** 人数（この指標を観測した対象の数） */
  n: number
  mean: number
  /** 標本標準偏差（不偏、分母 n − 1） */
  sd: number
}

/** 1 群の推定結果。 */
export interface MeanEstimate extends SampleSummary {
  /** 平均の標準誤差 sd / √n */
  standardError: number
  /** 平均の信頼区間 */
  interval: Interval
}

export interface MeanComparisonOptions {
  confidence: number
  tail: Tail
  /** 多重比較補正後の有意水準。指定すると区間の臨界値もこの水準で計算する。 */
  adjustedAlpha?: number | undefined
}

export interface MeanComparison {
  control: MeanEstimate
  variant: MeanEstimate
  /** variant の平均 − control の平均 */
  absoluteDiff: number
  absoluteDiffInterval: Interval
  /** 相対リフト。対照群の平均が 0 以下なら null。 */
  relativeLift: number | null
  relativeLiftInterval: Interval | null
  /** 検定統計量。両群のばらつきが 0 の縮退ケースでは null。 */
  t: number | null
  /** Welch-Satterthwaite の自由度 */
  df: number
  pValue: number | null
  /** 差の標準誤差 √(s1²/n1 + s2²/n2) */
  standardError: number
  /** 区間に使った臨界値 */
  criticalValue: number
}

/** 標本が検定に使える形かどうか。 */
export function isUsableSample(sample: SampleSummary): boolean {
  return (
    Number.isFinite(sample.n) &&
    sample.n >= 2 &&
    Number.isFinite(sample.mean) &&
    Number.isFinite(sample.sd) &&
    sample.sd >= 0
  )
}

/** 平均と、その信頼区間。区間には自由度 n − 1 の t 分布を使う。 */
export function estimateMean(
  sample: SampleSummary,
  confidence: number,
): MeanEstimate {
  const { n, mean, sd } = sample
  if (!isUsableSample(sample)) {
    return {
      ...sample,
      standardError: NaN,
      interval: { low: NaN, high: NaN },
    }
  }
  const standardError = sd / Math.sqrt(n)
  const critical = studentTQuantile(1 - (1 - confidence) / 2, n - 1)
  return {
    n,
    mean,
    sd,
    standardError,
    interval: {
      low: mean - critical * standardError,
      high: mean + critical * standardError,
    },
  }
}

/**
 * Welch-Satterthwaite の自由度。
 *
 * df = (s1²/n1 + s2²/n2)² / ( (s1²/n1)²/(n1−1) + (s2²/n2)²/(n2−1) )
 *
 * 両群のばらつきが 0 だと分母も分子も 0 になるため、その場合は NaN を返す。
 */
export function welchDegreesOfFreedom(
  control: SampleSummary,
  variant: SampleSummary,
): number {
  const v1 = (control.sd * control.sd) / control.n
  const v2 = (variant.sd * variant.sd) / variant.n
  const numerator = (v1 + v2) ** 2
  const denominator =
    (v1 * v1) / (control.n - 1) + (v2 * v2) / (variant.n - 1)
  if (denominator <= 0) return NaN
  return numerator / denominator
}

/** 点推定値と誤差幅から、裾に応じた区間を組み立てる。 */
function buildInterval(estimate: number, margin: number, tail: Tail): Interval {
  switch (tail) {
    case 'two-sided':
      return { low: estimate - margin, high: estimate + margin }
    case 'greater':
      return { low: estimate - margin, high: Infinity }
    case 'less':
      return { low: -Infinity, high: estimate + margin }
  }
}

/** t 統計量から選択した裾に応じた p 値を返す。 */
export function pValueFromT(t: number, df: number, tail: Tail): number {
  switch (tail) {
    case 'two-sided':
      return studentTTwoSided(t, df)
    case 'greater':
      return studentTCdf(-t, df)
    case 'less':
      return studentTCdf(t, df)
  }
}

/**
 * 2 群の平均の差の検定（Welch）。
 *
 * @param control 対照群
 * @param variant 比較したい群
 */
export function compareMeans(
  control: SampleSummary,
  variant: SampleSummary,
  options: MeanComparisonOptions,
): MeanComparison {
  const { confidence, tail } = options
  const alpha = options.adjustedAlpha ?? 1 - confidence
  const effectiveConfidence = 1 - alpha

  const c = estimateMean(control, effectiveConfidence)
  const v = estimateMean(variant, effectiveConfidence)

  const usable = isUsableSample(control) && isUsableSample(variant)
  const standardError = usable
    ? Math.sqrt(
        (control.sd * control.sd) / control.n +
          (variant.sd * variant.sd) / variant.n,
      )
    : NaN
  const df = usable ? welchDegreesOfFreedom(control, variant) : NaN
  const absoluteDiff = variant.mean - control.mean

  // 両群ともばらつきが 0 だと標準誤差が 0 になり、t が定義できない
  const t =
    usable && standardError > 0 && Number.isFinite(df)
      ? absoluteDiff / standardError
      : null
  const pValue = t === null ? null : pValueFromT(t, df, tail)

  const critical =
    usable && Number.isFinite(df)
      ? tail === 'two-sided'
        ? studentTQuantile(1 - alpha / 2, df)
        : studentTQuantile(1 - alpha, df)
      : NaN

  return {
    control: c,
    variant: v,
    absoluteDiff,
    absoluteDiffInterval: buildInterval(
      absoluteDiff,
      critical * standardError,
      tail,
    ),
    relativeLift: control.mean > 0 ? variant.mean / control.mean - 1 : null,
    relativeLiftInterval: logRatioInterval(control, variant, critical, tail),
    t,
    df,
    pValue,
    standardError,
    criticalValue: critical,
  }
}

/**
 * 平均の比の信頼区間。
 *
 * 比の分布は右に歪むため対数スケールで区間を作って戻す。
 * delta 法により Var(log m̂) ≈ (s²/n) / m²。
 * 平均が 0 をまたぐ指標（利益など）では対数が使えないので null を返す。
 */
function logRatioInterval(
  control: SampleSummary,
  variant: SampleSummary,
  critical: number,
  tail: Tail,
): Interval | null {
  if (!isUsableSample(control) || !isUsableSample(variant)) return null
  if (control.mean <= 0 || variant.mean <= 0) return null
  if (!Number.isFinite(critical)) return null

  const logRatio = Math.log(variant.mean / control.mean)
  const seLog = Math.sqrt(
    (control.sd * control.sd) / (control.n * control.mean * control.mean) +
      (variant.sd * variant.sd) / (variant.n * variant.mean * variant.mean),
  )
  const interval = buildInterval(logRatio, critical * seLog, tail)
  return {
    low: Math.exp(interval.low) - 1,
    high: Math.exp(interval.high) - 1,
  }
}

export interface MeanMdeInput {
  /** 差の標準誤差 */
  standardError: number
  alpha: number
  /** 目標検出力 (例: 0.8) */
  power: number
  tail: Tail
}

/**
 * この人数で検出できる最小の差（平均の場合）。
 *
 * MDE = (z_α + z_β) × 標準誤差。
 * 比率の MDE と違い正規近似を使うのは、t 検定の検出力が非心 t 分布に
 * なるためで、自由度が小さいと実際よりやや小さめの値が出る。
 * 「この人数では判断できない」という切り分けが目的なので、
 * 桁を誤らない範囲での近似で足りる。
 */
export function meanMde(input: MeanMdeInput): number | null {
  const { standardError, alpha, power, tail } = input
  if (!(standardError > 0) || !Number.isFinite(standardError)) return null
  if (!(alpha > 0) || alpha >= 1) return null
  if (power <= alpha || power >= 1) return null

  const zAlpha =
    tail === 'two-sided'
      ? normalQuantile(1 - alpha / 2)
      : normalQuantile(1 - alpha)
  const zBeta = normalQuantile(power)
  return (zAlpha + zBeta) * standardError
}
